import { AfterViewInit, Component, EventEmitter, Input, Output } from '@angular/core'
import { FormControl } from '@angular/forms'
import { SelectInputComponent } from '../inputs/select-input/select-input.component'
import { Country } from '../../types/country.type'
import { AppService } from '@app/v2/shared/services/app.service'

@Component({
  selector: 'country-select',
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
  standalone: true,
  imports: [SelectInputComponent]
})
export class CountrySelectComponent implements AfterViewInit {
  /**
   * @property control - form control for the country select
   */
  @Input() control!: FormControl
  /**
   * @property countries - list of countries to be displayed in the select
   */
  @Input() countries!: Country[]
  /**
   * @property label - label for the select field
   */
  @Input() label: string = 'Organization country'
  /**
   * @property valueName - value to be selected for country select
   * @example use id to select the id of the country / use name to select the name of the country / use {} to select the whole country object
   */
  @Input() valueName: string = '{}'
  /**
   * @property placeholder - placeholder for the select field
   */
  @Input() placeholder: string = ''
  /**
   * @property id - id for the select field
   */
  @Input() id: string = 'country-select'
  /**
   * @property clearButton - clearButton for the select field
   */
  @Input() clearButton: boolean = true
  /**
   * @property icon - icon for the select field
   */
  @Input()
  icon: string = `<svg class="tw-w-4 tw-h-4 tw-text-gray-800 dark:tw-text-white" fill="currentColor" viewBox="0 0 20 20">
    <path d="M10 .5a9.5 9.5 0 1 0 0 19 9.5 9.5 0 0 0 0-19ZM8.374 17.4a7.6 7.6 0 0 1-5.9-7.4c0-.83.137-1.655.406-2.441l.239.019a3.887 3.887 0 0 1 2.082 2.5 4.1 4.1 0 0 0 2.441 2.8c1.148.522 1.389 2.007.732 4.522Zm3.6-8.829a.997.997 0 0 0-.027-.225 5.456 5.456 0 0 0-2.811-3.662c-.832-.527-1.347-.854-1.486-1.89a7.584 7.584 0 0 1 8.364 2.47c-1.387.208-2.14 2.237-2.14 3.307a1.187 1.187 0 0 1-1.9 0Zm1.626 8.053-.671-2.013a1.9 1.9 0 0 1 1.771-1.757l2.032.619a7.553 7.553 0 0 1-3.132 3.151Z"/>
  </svg>`
  /**
   * @property iconType - icon type for the select field
   */
  @Input() iconType: string = 'fill'
  /**
   * @property required - required for the select field
   */
  @Input() required: boolean = false
  /**
   * @property autocomplete - autocomplete for the select field
   */
  @Input() autocomplete: 'on' | 'off' = 'off'
  /**
   * @emits valueChanged - emits the value of the selected country
   */
  @Output() valueChanged: EventEmitter<FormControl> = new EventEmitter<FormControl>()
  @Output() onKeydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>()
  @Output() onBlurValueChange: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>()
  /**
   * @property filteredValues - list of countries to be displayed in the select
   */
  public filteredValues!: Country[]

  constructor(private _appService: AppService) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      const userCountry = this._appService.getCachedUserCountry()
      if (userCountry) {
        this.control.setValue(userCountry)
        this.control.markAsTouched()
        this.control.markAsDirty()
      }
    })
  }

  /**
   * @method onSearch - filter countries by name, arabic name, alternatives, iso2, iso3
   * @param value string
   * @returns void
   */
  public onSearch(value: string): void {
    this.filteredValues = this.countries?.filter(
      (country) =>
        country.name.toLowerCase().includes(value) ||
        (country.alternatives && country.alternatives.split(',').includes(value)) ||
        country.iso2.toLowerCase().includes(value) ||
        country.iso3.toLowerCase().includes(value)
    )
  }

  /**
   * @method onValueChange - emits the value of the selected country
   * @returns void
   */
  public onValueChange(): void {
    this.valueChanged.emit(this.control)
  }

  /**
   * Forward keydown event to parent
   * @param {KeyboardEvent} event
   */
  public onKeydownEvent(event: KeyboardEvent): void {
    this.onKeydown.emit(event)
  }

  /**
   * Forward blur event to parent
   * @param {FocusEvent} event
   */
  public onBlurEvent(event: FocusEvent): void {
    this.onBlurValueChange.emit(event)
  }
}
